import { useState } from 'react';
import { FileText, Mail, Send, Calendar, Plus, Trash2 } from 'lucide-react';
import NoticeForm from '@/components/admin/NoticeForm';

const CommsTab = ({ contacts, notices, emails, showNoticeForm, setShowNoticeForm, handleCreateNotice, handleDeleteNotice, fmt }) => {
  const [subTab, setSubTab] = useState('notices');

  return (
    <div>
      <div className="flex gap-2 mb-6">
        {[
          { key: 'notices', label: `Notices (${notices.length})`, icon: FileText },
          { key: 'messages', label: `Messages (${contacts.length})`, icon: Mail },
          { key: 'emails', label: `Email Log (${emails.length})`, icon: Send },
        ].map(({ key, label, icon: Icon }) => (
          <button key={key} onClick={() => setSubTab(key)}
            className={`px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider transition-all flex items-center gap-1.5 ${subTab === key ? 'bg-[#C4A35A] text-[#0F1B2D]' : 'border border-[#D8D3CB] text-[#9B9589] hover:border-[#0F1B2D]'}`}
            data-testid={`comms-tab-${key}`}
          >
            <Icon size={12} />{label}
          </button>
        ))}
      </div>

      {/* Notices */}
      {subTab === 'notices' && (
        <div>
          {!showNoticeForm ? (
            <button onClick={() => setShowNoticeForm(true)} className="btn-ainaa-primary px-5 py-2.5 text-xs font-semibold uppercase tracking-wider flex items-center gap-2 mb-6" data-testid="add-notice-btn"><Plus size={14} />Add Notice</button>
          ) : (
            <NoticeForm onSubmit={handleCreateNotice} onCancel={() => setShowNoticeForm(false)} />
          )}
          <div className="space-y-0">
            {notices.map((notice) => (
              <div key={notice.id} className="border-b border-[#D8D3CB] first:border-t py-4 px-4 flex items-start gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <span className="text-[9px] font-bold uppercase tracking-wider text-[#9B9589]">{notice.ref_id}</span>
                    <span className={`text-[8px] font-bold uppercase tracking-widest px-2 py-0.5 ${notice.category === 'Circular' ? 'bg-[#0F1B2D] text-[#F5F3EE]' : 'bg-[#C4A35A] text-[#0F1B2D]'}`}>{notice.category}</span>
                    {notice.file_url && <FileText size={12} className="text-[#C4A35A]" />}
                  </div>
                  <h4 className="text-sm font-bold text-[#0F1B2D]">{notice.title}</h4>
                  <p className="text-xs text-[#9B9589] mt-1">{notice.date}</p>
                </div>
                <button onClick={() => handleDeleteNotice(notice.id)} className="p-1.5 text-[#D8D3CB] hover:text-red-500 transition-colors flex-shrink-0" data-testid={`delete-notice-${notice.id}`}><Trash2 size={14} /></button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Messages */}
      {subTab === 'messages' && (
        <div className="space-y-3">
          {contacts.length === 0 ? (
            <div className="border border-[#D8D3CB] bg-white p-12 text-center"><Mail className="w-10 h-10 text-[#D8D3CB] mx-auto mb-3" /><p className="text-[#9B9589] font-medium text-sm">No messages yet</p></div>
          ) : contacts.map((msg) => (
            <div key={msg.id} className="border border-[#D8D3CB] bg-white p-5">
              <div className="flex items-start justify-between mb-3">
                <div><h3 className="font-semibold text-sm text-[#0F1B2D]">{msg.subject}</h3><p className="text-xs text-[#9B9589]">{msg.name} ({msg.email})</p></div>
                <div className="flex items-center gap-1.5 text-xs text-[#9B9589]"><Calendar size={12} /><span>{fmt(msg.created_at)}</span></div>
              </div>
              <p className="text-sm text-[#4A4640] leading-relaxed">{msg.message}</p>
            </div>
          ))}
        </div>
      )}

      {/* Email Log */}
      {subTab === 'emails' && (
        <div className="space-y-2">
          <p className="text-[10px] uppercase tracking-[0.2em] text-[#9B9589] font-bold mb-4">
            Mock Email Log — Emails are logged here, not sent.
          </p>
          {emails.length === 0 ? (
            <div className="border border-[#D8D3CB] bg-white p-12 text-center"><Send className="w-10 h-10 text-[#D8D3CB] mx-auto mb-3" /><p className="text-[#9B9589] font-medium text-sm">No emails logged yet</p></div>
          ) : emails.map((em) => (
            <div key={em.id} className="border border-[#D8D3CB] bg-white p-4">
              <div className="flex items-start justify-between gap-3 mb-2">
                <div>
                  <p className="text-sm font-semibold text-[#0F1B2D]">{em.subject}</p>
                  <p className="text-xs text-[#9B9589]">To: {em.to}</p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <span className={`text-[8px] font-bold uppercase tracking-wider px-2 py-0.5 ${em.type?.includes('approved') ? 'bg-emerald-50 text-emerald-700' : em.type?.includes('rejected') ? 'bg-red-50 text-red-700' : 'bg-blue-50 text-blue-700'}`}>
                    {em.type?.replace('status_', '').replace('application_', '') || 'email'}
                  </span>
                  <span className="text-[10px] text-[#9B9589]">{fmt(em.created_at)}</span>
                </div>
              </div>
              <pre className="text-xs text-[#4A4640] leading-relaxed whitespace-pre-wrap font-[inherit]">{em.body}</pre>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CommsTab;
